import {
  PublicTagCategories,
  type PublicTagCategory,
  TagCategories,
  type TagCategory,
} from '@mntn-dev/domain-types'
import { useTranslation } from '@mntn-dev/i18n'
import type { TFunction } from 'i18next'

type TagVisibility = 'all' | 'public'

type TagTranslations = {
  singular: string
  plural: string
  plusMoreTextFactory: (category: TagCategory) => (count: number) => string
}

type AllTagTranslations = {
  type: 'all'
  data: Record<TagCategory, TagTranslations>
}

type PublicTagTranslations = {
  type: 'public'
  data: Record<PublicTagCategory, TagTranslations>
}

type TagTranslationData = AllTagTranslations | PublicTagTranslations

const getTagTranslations = <T extends TagVisibility>(
  visibility: T,
  t: TFunction<'tags', undefined>
) => {
  const categories =
    visibility === 'public' ? PublicTagCategories : TagCategories
  const translationData = categories.reduce<
    Record<TagCategory, TagTranslations>
  >(
    (acc, category) => {
      acc[category] = {
        singular: t(`category-labels.${category}`, { count: 1 }),
        plural: t(`category-labels.${category}`, { count: 2 }),
        plusMoreTextFactory: (category: TagCategory) => {
          return (count: number) => {
            const categoryText = t(`category-labels.${category}`, {
              count,
            }).toLowerCase()
            return t('plus-more-format', { count, category: categoryText })
          }
        },
      }
      return acc
    },
    {} as Record<TagCategory, TagTranslations>
  )

  return { type: 'all', data: translationData } as Extract<
    TagTranslationData,
    { type: T }
  >
}

function useTagCategories<T extends TagVisibility>(visibility: T) {
  const { t } = useTranslation(['tags'])

  const translations = getTagTranslations(visibility, t)

  return {
    translations,
  }
}

export { useTagCategories }
